/**
 * Service for interacting with Pusher API
 */
import axios from 'axios';
import logger from '../utils/logger';

// Use relative URLs with proxy configuration
const API_BASE = '';

// Log the API configuration on startup
logger.info('🔌 PUSHER SERVICE: Initialized with proxy endpoints:', {
  baseUrl: API_BASE,
  pusherEventsEndpoint: `/api/pusher/events`,
  typingEndpoint: `/api/debates/:debateId/typing`,
  messagesEndpoint: `/api/debates/:debateId/messages`,
  browserBaseUrl: window.location.origin
});

// Create an Axios instance with the correct configuration
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Log request details for debugging
apiClient.interceptors.request.use(config => {
  logger.info(`🔄 API REQUEST: ${config.method.toUpperCase()} ${config.url}`, {
    baseURL: config.baseURL,
    fullUrl: `${config.baseURL || ''}${config.url}`
  });
  return config;
});

/**
 * Send a message through the Pusher API
 * @param {string} channel - The channel to send the message to
 * @param {string} event - The event name
 * @param {object} data - The data to send
 * @returns {Promise} A promise that resolves when the message is sent
 */
export const sendPusherMessage = async (channel, event, data) => {
  // Extract debateId from channel name
  const debateId = channel.replace('debate-', '');
  if (!debateId || !channel.startsWith('debate-')) {
    logger.error('🔄 PUSHER: Invalid channel format, must be debate-{debateId}');
    throw new Error('Invalid channel format');
  }
  
  // Map Pusher events to our unified endpoint format
  let eventType = '';
  if (event === 'message_received') eventType = 'message';
  else if (event === 'user_typing') eventType = 'typing';
  else eventType = event;
  
  // Try all possible endpoints in sequence until one works
  const endpoints = [
    // First try the unified endpoint in debates (most reliable)
    {
      url: `/api/debates/pusher/events`,
      body: {
        channel,
        event,
        data
      }
    },
    // Then try the dedicated send-message endpoint in debates
    {
      url: `/api/debates/send-message`,
      body: {
        channel,
        message: data,
        user: data.user || data.userId,
        debateId
      }
    },
    // Legacy endpoint (kept as fallback)
    {
      url: `/api/pusher/events`,
      body: {
        channel,
        event,
        data
      }
    }
  ];
  
  // Log what we're trying to do
  logger.info(`🔄 PUSHER: Sending ${eventType} event to channel ${channel}`, {
    debateId,
    eventType,
    dataPreview: typeof data === 'object' ? {
      ...data,
      content: data.content?.substring(0, 30) + (data.content?.length > 30 ? '...' : '')
    } : data
  });
  
  // Try each endpoint in sequence
  for (const endpoint of endpoints) {
    try {
      logger.info(`🔄 PUSHER: Trying endpoint ${endpoint.url}`);
      
      const response = await apiClient.post(endpoint.url, endpoint.body);
      
      logger.info(`🔄 PUSHER: Successfully sent ${eventType} event to ${endpoint.url}`, {
        statusCode: response.status
      });
      
      // If we get here, the request succeeded
      return response.data;
    } catch (error) {
      logger.warn(`🔄 PUSHER: Failed with endpoint ${endpoint.url}:`, {
        message: error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Continue to the next endpoint
    }
  }
  
  // If we get here, all endpoints failed
  const errorMsg = `Failed to send ${event} event after trying all endpoints`;
  logger.error(`🔄 PUSHER ERROR: ${errorMsg}`);
  throw new Error(errorMsg);
};

/**
 * Send a message in a debate
 * @param {string} debateId - The ID of the debate
 * @param {string} content - The message content
 * @param {string} teamId - The ID of the team
 * @param {string} userId - The ID of the user
 * @param {boolean} isReformulation - Whether this is a reformulated message
 * @returns {Promise} A promise that resolves with the sent message
 */
export const sendDebateMessage = async (debateId, content, teamId, userId, isReformulation = false) => {
  try {
    logger.info(`Sending debate message to ${API_BASE}/api/debates/${debateId}/messages`);
    const response = await apiClient.post(`/api/debates/${debateId}/messages`, {
      content,
      teamId,
      userId,
      isReformulation
    });
    
    return response.data;
  } catch (error) {
    logger.error(`Error sending debate message:`, error);
    throw error;
  }
};

/**
 * Helper to generate a channel name for a debate
 * @param {string} debateId - The ID of the debate
 * @returns {string} The channel name
 */
export const getDebateChannelName = (debateId) => {
  return `debate-${debateId}`;
};

/**
 * Helper to generate a channel name for a user's private notifications
 * @param {string} userId - The ID of the user
 * @returns {string} The channel name
 */
export const getUserChannelName = (userId) => {
  return `user-${userId}`;
};

/**
 * Send a user typing notification
 * @param {string} debateId - The ID of the debate 
 * @param {string} userId - The ID of the user
 * @param {string} teamId - The ID of the team
 * @param {boolean} isTyping - Whether the user is typing
 * @returns {Promise} A promise that resolves when the notification is sent
 */
export const sendTypingNotification = async (debateId, userId, teamId, isTyping) => {
  if (!debateId || !userId) {
    logger.warn('🔄 TYPING: Missing required parameters for typing notification');
    return null;
  }
  
  // Try all possible endpoints in sequence until one works
  const endpoints = [
    // First try the unified endpoint in debates (most reliable)
    {
      url: `/api/debates/pusher/events`,
      body: {
        channel: `debate-${debateId}`,
        event: 'user_typing',
        data: {
          userId,
          teamId,
          isTyping
        }
      }
    },
    // Then try the dedicated typing endpoint (this one works)
    {
      url: `/api/debates/${debateId}/typing`,
      body: {
        user: { id: userId },
        isTyping
      }
    },
    // Finally try the legacy send-message endpoint in debates
    {
      url: `/api/debates/send-message`,
      body: {
        channel: `debate-${debateId}`,
        event: 'user_typing',
        data: {
          debateId,
          userId,
          teamId,
          isTyping
        }
      }
    }
  ];
  
  // Try each endpoint in sequence
  for (const endpoint of endpoints) {
    try {
      logger.info(`🔄 TYPING: Trying endpoint ${endpoint.url}`);
      
      const response = await apiClient.post(endpoint.url, endpoint.body);
      
      logger.info(`🔄 TYPING: Successfully sent typing notification to ${endpoint.url}`, {
        statusCode: response.status
      });
      
      // If we get here, the request succeeded
      return response.data;
    } catch (error) {
      logger.warn(`🔄 TYPING: Failed with endpoint ${endpoint.url}:`, {
        message: error.message,
        status: error.response?.status
      });
      
      // Continue to the next endpoint
    }
  }
  
  // If we get here, all endpoints failed
  logger.warn('🔄 TYPING: All typing notification endpoints failed');
  return null;
};

/**
 * Request a team to reformulate their message
 * @param {string} debateId - The ID of the debate
 * @param {string} teamId - The ID of the team that needs to reformulate
 * @param {boolean} selfInitiated - Whether this was initiated by the team themselves
 * @returns {Promise} A promise that resolves when the request is sent
 */
export const requestReformulation = async (debateId, teamId, selfInitiated = false) => {
  if (!debateId || !teamId) {
    logger.error('Missing required parameters for reformulation request');
    throw new Error('Missing required parameters for reformulation request');
  }
  
  try {
    const channel = getDebateChannelName(debateId);
    return await sendPusherMessage(channel, 'reformulation_request', {
      debateId,
      teamId,
      selfInitiated
    });
  } catch (error) {
    logger.error('Failed to request reformulation:', error);
    throw error;
  }
};

/**
 * Reset a reformulation request
 * @param {string} debateId - The ID of the debate
 * @returns {Promise} A promise that resolves when the reset is sent
 */
export const resetReformulation = async (debateId) => {
  if (!debateId) {
    logger.error('Missing debateId for reset reformulation');
    throw new Error('Missing debateId for reset reformulation');
  }
  
  try {
    const channel = getDebateChannelName(debateId);
    return await sendPusherMessage(channel, 'reformulation_reset', {
      debateId
    });
  } catch (error) {
    logger.error('Failed to reset reformulation:', error);
    throw error;
  }
};

const pusherService = {
  sendPusherMessage,
  sendDebateMessage,
  getDebateChannelName,
  getUserChannelName,
  sendTypingNotification,
  requestReformulation,
  resetReformulation
};

export default pusherService; 